import {
  useState,
  useEffect,
  ReactNode
} from 'react'
import { ThemeContext } from './UseThemeContext'

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // On initialization, try to read the stored theme mode from localStorage.
  const storedThemeMode = localStorage.getItem('themeMode') as
    | 'auto'
    | 'dark'
    | 'light'
    | null
  const initialThemeMode = storedThemeMode ?? 'auto'

  const [themeMode, setThemeMode] = useState<'auto' | 'dark' | 'light'>(
    initialThemeMode
  )

  // Helper: Given a mode, return whether the effective theme should be dark.
  const getEffectiveDark = (mode: 'auto' | 'dark' | 'light'): boolean => {
    if (mode === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return mode === 'dark'
  }

  // Store the effective dark/light state.
  const [isDark, setIsDark] = useState<boolean>(getEffectiveDark(themeMode))

  // Whenever themeMode or isDark changes, update localStorage and the document class.
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode)
    // For non-auto modes, ensure the effective theme matches the selected mode.
    if (themeMode !== 'auto') {
      setIsDark(themeMode === 'dark')
    }
    document.documentElement.classList.toggle('dark', isDark)
  }, [themeMode, isDark])

  // Listen for OS color-scheme changes when in auto mode.
  useEffect(() => {
    if (themeMode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDark(e.matches)
      }

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange)
      } else {
        // Fallback for older browsers.
        mediaQuery.addListener(handleChange)
      }
      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange)
        } else {
          mediaQuery.removeListener(handleChange)
        }
      }
    }
    // No listener needed if not in auto mode.
    return
  }, [themeMode])

  // Cycle through modes on toggle: auto → dark → light → auto.
  const toggleTheme = () => {
    if (themeMode === 'auto') {
      setThemeMode('dark')
      setIsDark(true)
    } else if (themeMode === 'dark') {
      setThemeMode('light')
      setIsDark(false)
    } else {
      setThemeMode('auto')
      // When switching back to auto, immediately update based on OS preference.
      setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches)
    }
  }

  // Additional state for your app (e.g., videos vs. ebooks)
  const [mode, setMode] = useState<'videos' | 'ebooks'>('videos')

  return (
    <ThemeContext.Provider
      value={{ isDark, themeMode, toggleTheme, setThemeMode, mode, setMode }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
